import { useState } from 'react';

export type DeleteTarget =
  | { type: 'folder'; id: string }
  | { type: 'doc'; id: string }
  | { type: 'version'; documentId: string; seq: number };

interface IUseDeleteConfirmationArgs {
  deleteFolder: (id: string) => Promise<void> | void;
  deleteDoc: (id: string) => Promise<void> | void;
  deleteVersion: (documentId: string, seq: number) => Promise<void> | void;
  onConfirmed?: (target: DeleteTarget) => void;
}

// Drives the delete-confirmation modal: the pending target, the in-flight state
// that keeps the modal open with a disabled confirm button until the upstream
// delete settles, and the confirm/cancel actions.
export function useDeleteConfirmation({ deleteFolder, deleteDoc, deleteVersion, onConfirmed }: IUseDeleteConfirmationArgs) {
  const [pendingDelete, setPendingDelete] = useState<DeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  function requestDelete(target: DeleteTarget) {
    setPendingDelete(target);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    // Deliberately optimistic: navigation away from the deleted target (reset
    // selection, close its drawer) happens immediately, not after the upstream
    // delete settles. On failure the delete hooks roll the rows back and toast,
    // but we don't navigate the user back into the restored target.
    onConfirmed?.(target);
    setIsDeleting(true);
    try {
      await (target.type === 'folder'
        ? deleteFolder(target.id)
        : target.type === 'doc'
          ? deleteDoc(target.id)
          : deleteVersion(target.documentId, target.seq));
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  }

  function cancelDelete() {
    setPendingDelete(null);
  }

  return { pendingDelete, isDeleting, requestDelete, confirmDelete, cancelDelete };
}